/**
 * Debutify 10-Item Order & Cart Limit Enforcement
 * 
 * Rules:
 * 1. Global Order Limit: Maximum 10 items total allowed per order across all products.
 * 2. Product Page:
 *    - Quantity selector cannot increment beyond remaining allowance (Math.max(0, 10 - cartTotalCount)).
 *    - Plus button is disabled when quantity reaches remaining allowance or cart is full.
 *    - Manual typing into quantity input is clamped to remaining allowance with an inline notice.
 *    - If cart has 10 items, quantity input, plus/minus buttons, and Add-to-Cart buttons are disabled with an inline notice.
 *    - Form submission is synchronously intercepted if it would exceed 10 items total.
 * 3. Cart Drawer & Cart Details Page:
 *    - All plus buttons are disabled when cart total reaches 10 items.
 *    - Manual typing into line item quantity is clamped so total never exceeds 10 items.
 *    - Inline warning is displayed if cart total > 10, and checkout is disabled.
 * 4. Zero native browser popups (dialog boxes): All feedback is 100% inline.
 */

(function() {
  'use strict';

  const MAX_CART_LIMIT = 10;
  let currentCartCount = 0;
  let isCartLoaded = false;

  // 1. Inject styling immediately
  if (!document.getElementById('cart-limit-styles')) {
    const style = document.createElement('style');
    style.id = 'cart-limit-styles';
    style.textContent = `
      .quantity__button.disabled,
      .quantity__button:disabled,
      .quantity__button[disabled] {
        opacity: 0.35 !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
      }
      .cart-limit-product-notice {
        color: #b00020;
        font-size: 1.25rem;
        line-height: 1.4;
        margin-top: 0.8rem;
        margin-bottom: 0.8rem;
        font-weight: 500;
        padding: 0.6rem 1rem;
        background-color: #fdf2f2;
        border: 1px solid #f8b4b4;
        border-radius: 4px;
        display: block;
        animation: cartLimitFadeIn 0.25s ease-out;
      }
      .cart-limit-product-notice.hidden {
        display: none !important;
      }
      .cart-order-limit-warning {
        color: #b00020;
        font-size: 1.3rem;
        margin-top: 0.8rem;
        text-align: center;
        font-weight: 600;
      }
      .cart-order-limit-warning.hidden {
        display: none !important;
      }
      @keyframes cartLimitFadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }

  // 2. Helper to get cart count from DOM if available
  function getCartItemCountFromDOM() {
    const bubble = document.querySelector('.cart-count-bubble span:first-child');
    if (bubble) {
      const parsed = parseInt(bubble.textContent.trim(), 10);
      if (!isNaN(parsed)) return parsed;
    }
    const drawerInputs = document.querySelectorAll('cart-drawer-items input[name="updates[]"], cart-items input[name="updates[]"]');
    if (drawerInputs.length > 0) {
      let sum = 0;
      drawerInputs.forEach(inp => {
        sum += (parseInt(inp.value, 10) || 0);
      });
      return sum;
    }
    return currentCartCount;
  }

  // 3. Sync Product Page Form & Inputs
  function syncProductForms(cartCount) {
    const remaining = Math.max(0, MAX_CART_LIMIT - cartCount);

    const productQtyInputs = document.querySelectorAll(
      'product-info input[name="quantity"], product-form input[name="quantity"], .quick-add input[name="quantity"], .product-form__quantity input[name="quantity"]'
    );

    productQtyInputs.forEach(input => {
      input.setAttribute('data-cart-limit', MAX_CART_LIMIT);
      input.setAttribute('data-cart-total', cartCount);
      input.setAttribute('data-remaining-limit', remaining);

      const qtyWrapper = input.closest('quantity-input');
      const minusBtn = qtyWrapper ? qtyWrapper.querySelector('button[name="minus"]') : null;
      const plusBtn = qtyWrapper ? qtyWrapper.querySelector('button[name="plus"]') : null;
      const productForm = input.closest('product-form') || (input.form ? input.form.closest('product-form') : null);
      const submitBtn = productForm ? productForm.querySelector('button[type="submit"][name="add"], .product-form__submit') : null;
      const dynamicCheckout = productForm ? productForm.querySelector('.shopify-payment-button') : null;

      let noticeEl = input.closest('.product-form__input')?.querySelector('.cart-limit-product-notice');
      if (!noticeEl && productForm) {
        noticeEl = productForm.querySelector('.cart-limit-product-notice');
      }

      if (remaining <= 0) {
        // Cart is full (10 items already)
        input.value = 1;
        input.setAttribute('disabled', 'disabled');
        input.disabled = true;

        if (plusBtn) {
          plusBtn.setAttribute('disabled', 'disabled');
          plusBtn.classList.add('disabled');
        }
        if (minusBtn) {
          minusBtn.setAttribute('disabled', 'disabled');
          minusBtn.classList.add('disabled');
        }
        if (submitBtn) {
          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('disabled');
          submitBtn.setAttribute('data-cart-limit-disabled', 'true');
        }
        if (dynamicCheckout) {
          dynamicCheckout.style.display = 'none';
        }

        // Cleanly handle button notice vs quantity notice
        const btnNotice = productForm ? productForm.querySelector('.cart-limit-button-notice') : document.querySelector('.cart-limit-button-notice');
        if (noticeEl) {
          noticeEl.style.display = 'none';
          noticeEl.classList.add('hidden');
        }
        if (btnNotice) {
          btnNotice.textContent = `Order limit reached: Your cart already contains ${MAX_CART_LIMIT} items (maximum allowed per order).`;
          btnNotice.style.display = 'block';
        } else if (productForm) {
          const div = document.createElement('div');
          div.className = 'cart-limit-button-notice';
          div.style.cssText = 'color: #b00020; font-size: 1.25rem; line-height: 1.4; margin-top: 0.8rem; margin-bottom: 0.8rem; font-weight: 500; padding: 0.6rem 1rem; background-color: #fdf2f2; border: 1px solid #f8b4b4; border-radius: 4px; text-align: center;';
          div.textContent = `Order limit reached: Your cart already contains ${MAX_CART_LIMIT} items (maximum allowed per order).`;
          const buttonsContainer = productForm.querySelector('.product-form__buttons') || productForm;
          buttonsContainer.after(div);
        }
      } else {
        // Cart has space for items
        const btnNotice = productForm ? productForm.querySelector('.cart-limit-button-notice') : document.querySelector('.cart-limit-button-notice');
        if (btnNotice) {
          btnNotice.style.display = 'none';
        }
        input.removeAttribute('disabled');
        input.disabled = false;
        input.setAttribute('max', remaining);
        input.max = remaining;

        const currentVal = parseInt(input.value, 10) || 1;
        if (currentVal > remaining) {
          input.value = remaining;
        }

        const effectiveVal = parseInt(input.value, 10) || 1;

        if (plusBtn) {
          if (effectiveVal >= remaining) {
            plusBtn.setAttribute('disabled', 'disabled');
            plusBtn.classList.add('disabled');
          } else {
            plusBtn.removeAttribute('disabled');
            plusBtn.classList.remove('disabled');
          }
        }
        if (minusBtn) {
          if (effectiveVal <= 1) {
            minusBtn.setAttribute('disabled', 'disabled');
            minusBtn.classList.add('disabled');
          } else {
            minusBtn.removeAttribute('disabled');
            minusBtn.classList.remove('disabled');
          }
        }
        if (submitBtn && submitBtn.hasAttribute('data-cart-limit-disabled')) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('disabled');
          submitBtn.removeAttribute('data-cart-limit-disabled');
        }
        if (dynamicCheckout) {
          dynamicCheckout.style.display = '';
        }

        if (noticeEl) {
          noticeEl.classList.add('hidden');
          noticeEl.style.display = 'none';
        }
      }
    });
  }

  // 4. Sync Cart Drawer & Cart Details Page
  function syncCartContainers(cartCount) {
    if (typeof Debutify !== 'undefined') {
      Debutify.checkout = Debutify.checkout || {};
      const withinLimit = (cartCount <= MAX_CART_LIMIT);
      Debutify.checkout['max-order-limit'] = withinLimit;
      if (typeof checkoutButtonHandler === 'function') {
        checkoutButtonHandler();
      }
    }

    const warnings = document.querySelectorAll('#cart-order-limit-warning, .cart-order-limit-warning');
    warnings.forEach(warning => {
      if (cartCount > MAX_CART_LIMIT) {
        const excess = cartCount - MAX_CART_LIMIT;
        warning.textContent = `Order limit: Maximum of ${MAX_CART_LIMIT} items allowed per order. Please remove ${excess} item(s) to proceed.`;
        warning.classList.remove('hidden');
        warning.style.display = 'block';
      } else {
        warning.classList.add('hidden');
        warning.style.display = 'none';
      }
    });

    const cartContainers = document.querySelectorAll('cart-drawer-items, cart-items');
    cartContainers.forEach(container => {
      const inputs = container.querySelectorAll('input[name="updates[]"], .custom_quantity__input');
      let totalInContainer = 0;
      inputs.forEach(inp => {
        totalInContainer += (parseInt(inp.value, 10) || 0);
      });

      const effectiveTotal = Math.max(cartCount, totalInContainer);
      const slotsRemaining = Math.max(0, MAX_CART_LIMIT - effectiveTotal);

      inputs.forEach(input => {
        const qtyWrapper = input.closest('quantity-input');
        const plusBtn = qtyWrapper ? qtyWrapper.querySelector('button[name="plus"]') : null;
        const minusBtn = qtyWrapper ? qtyWrapper.querySelector('button[name="minus"]') : null;
        const currentLineVal = parseInt(input.value, 10) || 0;
        const lineMax = currentLineVal + slotsRemaining;

        input.setAttribute('max', lineMax);
        input.max = lineMax;

        if (plusBtn) {
          if (slotsRemaining <= 0 || currentLineVal >= lineMax) {
            plusBtn.setAttribute('disabled', 'disabled');
            plusBtn.classList.add('disabled');
          } else {
            plusBtn.removeAttribute('disabled');
            plusBtn.classList.remove('disabled');
          }
        }

        if (minusBtn) {
          if (currentLineVal <= 0) {
            minusBtn.setAttribute('disabled', 'disabled');
            minusBtn.classList.add('disabled');
          } else {
            minusBtn.removeAttribute('disabled');
            minusBtn.classList.remove('disabled');
          }
        }
      });
    });
  }

  // 5. Unified update
  function updateAllCartLimits(count) {
    if (typeof count === 'number') {
      currentCartCount = count;
    } else if (!isCartLoaded) {
      currentCartCount = getCartItemCountFromDOM();
    }
    syncProductForms(currentCartCount);
    syncCartContainers(currentCartCount);
  }

  // 6. Show clean inline product notification
  function showProductNotice(contextEl, message) {
    const container = contextEl.closest('product-info, product-form, .product-form__input, .product-form__quantity') || contextEl.parentElement;
    if (!container) return;

    let notice = container.querySelector('.cart-limit-product-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'cart-limit-product-notice';
      const insertRef = container.querySelector('.price-per-item__container') || container.querySelector('.product-form__buttons') || container.firstElementChild;
      if (insertRef && insertRef.parentElement) {
        insertRef.parentElement.insertBefore(notice, insertRef.nextSibling);
      } else {
        container.appendChild(notice);
      }
    }
    notice.textContent = message;
    notice.classList.remove('hidden');
    notice.style.display = 'block';

    clearTimeout(notice._timer);
    notice._timer = setTimeout(() => {
      if (currentCartCount < MAX_CART_LIMIT) {
        notice.classList.add('hidden');
        notice.style.display = 'none';
      }
    }, 4500);
  }

  // 7. Plus Button Click Interception (disables increment, inline feedback only)
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('button[name="plus"]');
    if (!btn) return;
    const qtyWrapper = btn.closest('quantity-input');
    if (!qtyWrapper) return;
    const input = qtyWrapper.querySelector('input');
    if (!input) return;

    // Check if in Cart Drawer / Cart Page
    const inCart = qtyWrapper.closest('cart-items, cart-drawer-items');
    if (inCart) {
      if (currentCartCount >= MAX_CART_LIMIT) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('disabled');

        const warning = document.querySelector('#cart-order-limit-warning, .cart-order-limit-warning');
        if (warning) {
          warning.textContent = `Order limit reached: Maximum of ${MAX_CART_LIMIT} items allowed per order.`;
          warning.classList.remove('hidden');
          warning.style.display = 'block';
        }
        return false;
      }
      return;
    }

    // Otherwise, on Product Page
    const remaining = Math.max(0, MAX_CART_LIMIT - currentCartCount);
    const currentVal = parseInt(input.value, 10) || 1;
    if (currentVal >= remaining) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('disabled');

      showProductNotice(
        qtyWrapper,
        remaining <= 0
          ? `Order limit reached: Your cart already has ${MAX_CART_LIMIT} items.`
          : `Order limit: You can only add up to ${remaining} item(s) (10 items total allowed per order).`
      );
      return false;
    }
  }, true);

  // 8. Manual Typing Interception (clamp immediately, inline feedback only)
  function handleQtyChange(e) {
    const input = e.target.closest('input[name="quantity"], input[name="updates[]"], .custom_quantity__input');
    if (!input) return;

    const inCart = input.closest('cart-items, cart-drawer-items');
    if (inCart) {
      // In cart drawer / cart page
      const allInputs = inCart.querySelectorAll('input[name="updates[]"], .custom_quantity__input');
      let otherTotal = 0;
      allInputs.forEach(other => {
        if (other !== input) otherTotal += (parseInt(other.value, 10) || 0);
      });
      const maxForLine = Math.max(0, MAX_CART_LIMIT - otherTotal);
      let val = parseInt(input.value, 10);
      if (!isNaN(val) && val > maxForLine) {
        input.value = maxForLine;
        const warning = document.querySelector('#cart-order-limit-warning, .cart-order-limit-warning');
        if (warning) {
          warning.textContent = `Order limit: Maximum of ${MAX_CART_LIMIT} items allowed per order.`;
          warning.classList.remove('hidden');
          warning.style.display = 'block';
        }
      }
      return;
    }

    // On product page
    const remaining = Math.max(0, MAX_CART_LIMIT - currentCartCount);
    let val = parseInt(input.value, 10);
    if (!isNaN(val)) {
      if (val > remaining) {
        input.value = remaining;
        showProductNotice(input, `Quantity capped at ${remaining}. Maximum 10 items total allowed per order.`);
      } else if (val < 1 && input.value !== '') {
        input.value = 1;
      }
    }
    const qtyWrapper = input.closest('quantity-input');
    const plusBtn = qtyWrapper ? qtyWrapper.querySelector('button[name="plus"]') : null;
    if (plusBtn) {
      const cur = parseInt(input.value, 10) || 1;
      if (cur >= remaining) {
        plusBtn.setAttribute('disabled', 'disabled');
        plusBtn.classList.add('disabled');
      } else {
        plusBtn.removeAttribute('disabled');
        plusBtn.classList.remove('disabled');
      }
    }
  }

  document.addEventListener('input', handleQtyChange, true);
  document.addEventListener('change', handleQtyChange, true);

  // 9. Synchronous Add-to-Cart Submission Interception (100% inline notification)
  document.addEventListener('submit', function(e) {
    const form = e.target.closest('form[action*="/cart/add"], [data-type="add-to-cart-form"]');
    if (!form) return;

    const qtyInput = form.querySelector('[name="quantity"]');
    const addingQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    const remaining = Math.max(0, MAX_CART_LIMIT - currentCartCount);

    if (currentCartCount + addingQty > MAX_CART_LIMIT) {
      e.preventDefault();
      e.stopImmediatePropagation();

      const productFormEl = form.closest('product-form');
      const errorMsg = (remaining <= 0)
        ? `Order limit reached: Your cart already has ${MAX_CART_LIMIT} items (maximum allowed per order).`
        : `Order limit exceeded: You can only add up to ${remaining} more item(s) (10 items total allowed per order).`;

      if (productFormEl && typeof productFormEl.handleErrorMessage === 'function') {
        productFormEl.handleErrorMessage(errorMsg);
      } else {
        showProductNotice(form, errorMsg);
      }

      if (remaining > 0 && qtyInput) {
        qtyInput.value = remaining;
      }

      return false;
    }
  }, true);

  // 10. Fetch Cart State & Sync
  function fetchCartAndSync() {
    fetch(`${window.routes?.cart_url || '/cart'}.js?_=${Date.now()}`)
      .then(res => res.json())
      .then(cart => {
        isCartLoaded = true;
        updateAllCartLimits(cart.item_count);
      })
      .catch(() => {});
  }

  // 11. Fetch Interceptor for immediate state sync on any AJAX cart mutation
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    const isCartMutation = url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear');

    return originalFetch.apply(this, args).then(response => {
      if (isCartMutation && response.ok) {
        response.clone().json().then(data => {
          if (typeof data.item_count === 'number') {
            updateAllCartLimits(data.item_count);
          } else {
            fetchCartAndSync();
          }
        }).catch(() => {
          fetchCartAndSync();
        });
      }
      return response;
    });
  };

  // 12. Subscribe to Debutify PubSub Events
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
      const itemCount = event?.cartData?.item_count;
      if (typeof itemCount === 'number') {
        updateAllCartLimits(itemCount);
      } else {
        fetchCartAndSync();
      }
    });

    if (PUB_SUB_EVENTS.cartTotalRefreshed) {
      subscribe(PUB_SUB_EVENTS.cartTotalRefreshed, function() {
        fetchCartAndSync();
      });
    }

    if (PUB_SUB_EVENTS.quantityUpdate) {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, function() {
        updateAllCartLimits(currentCartCount);
      });
    }
  }

  // 13. DOM Mutation Observer for dynamically rendered drawers
  const observer = new MutationObserver(function() {
    updateAllCartLimits(currentCartCount);
  });

  function init() {
    updateAllCartLimits();
    fetchCartAndSync();

    const drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('focus', fetchCartAndSync);
})();

